import { Link } from "react-router-dom";
import {
  CloudSun, Gavel, ListChecks, LogOut, Shield, Sparkles, Sprout, Stethoscope, Store,
  TrendingUp, Truck, Truck as TruckIcon, Users, BookOpen, Bell, Beef, Map, Heart,
  LayoutDashboard, Calculator, LogIn, UserPlus, CalendarDays, Moon,
} from "lucide-react";
import { BottomSheet } from "@/components/mobile/BottomSheet";

const Item = ({ to, icon: Icon, label, testid, onClose }) => (
  <Link
    to={to}
    onClick={onClose}
    data-testid={testid}
    className="app-tap flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-[15px] font-semibold text-primary hover:bg-muted"
  >
    <Icon className="h-5 w-5 shrink-0 text-secondary" />
    {label}
  </Link>
);

const Group = ({ title, children }) => (
  <>
    <p className="mt-4 px-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      {title}
    </p>
    <div className="grid gap-0.5">{children}</div>
  </>
);

const STAFF = ["Moderator", "SupportAgent", "Admin", "SuperAdmin"];

/** Hamburger menüsünün mobil karşılığı: alttan açılan tam menü paneli. */
export const MobileMenuSheet = ({ open, onClose, cats, user, isVet, onLogout }) => (
  <BottomSheet open={open} onClose={onClose} title="Menü" testid="mobile-menu">
    {!user && (
      <div className="grid grid-cols-2 gap-2">
        <Link
          to="/giris"
          onClick={onClose}
          data-testid="sheet-login"
          className="app-tap flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-primary text-sm font-bold text-primary"
        >
          <LogIn className="h-4 w-4" /> Giriş Yap
        </Link>
        <Link
          to="/kayit"
          onClick={onClose}
          data-testid="sheet-register"
          className="app-tap flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground"
        >
          <UserPlus className="h-4 w-4" /> Üye Ol
        </Link>
      </div>
    )}

    <Group title="Pazar">
      <Item to="/ilanlar" icon={Store} label="Tüm İlanlar" testid="sheet-nav-listings" onClose={onClose} />
      <Item to="/mezat" icon={Gavel} label="Ciftora Mezat" testid="mobile-nav-auctions" onClose={onClose} />
      <Item to="/magazalar" icon={Store} label="Mağazalar" testid="mobile-nav-stores" onClose={onClose} />
      <Item to="/nakliye" icon={Truck} label="Nakliye Ağı" testid="mobile-nav-transport" onClose={onClose} />
      <Item to="/kasap-bul" icon={Beef} label="Kasap & Kesimhane" testid="mobile-nav-butcher" onClose={onClose} />
      <Item to="/degerleme" icon={Calculator} label="Değerleme" testid="mobile-nav-valuation" onClose={onClose} />
    </Group>

    <Group title="Piyasa & Bilgi">
      <Item to="/fiyatlar" icon={TrendingUp} label="Ciftora Piyasa" testid="mobile-nav-market-prices" onClose={onClose} />
      <Item to="/fiyat-haritasi" icon={Map} label="Fiyat Haritası" testid="mobile-nav-market-map" onClose={onClose} />
      <Item to="/fuarlar" icon={CalendarDays} label="Fuar Takvimi" testid="mobile-nav-fairs" onClose={onClose} />
      <Item to="/kurban" icon={Moon} label="Kurban Market" testid="mobile-nav-sacrifice" onClose={onClose} />
      <Item to="/kurbanim" icon={Moon} label="Kurbanım" testid="mobile-nav-my-sacrifice" onClose={onClose} />
      <Item to="/hava" icon={CloudSun} label="Ciftora Hava" testid="mobile-nav-weather" onClose={onClose} />
      <Item to="/bilgi-merkezi" icon={BookOpen} label="Bilgi Merkezi" testid="mobile-nav-knowledge" onClose={onClose} />
      <Item to="/ciftora-ai" icon={Sparkles} label="Ciftora AI" testid="mobile-nav-ai" onClose={onClose} />
    </Group>

    <Group title="Çiftlik & Hizmet">
      <Item to="/ciftligim" icon={Sprout} label="Çiftliğim" testid="mobile-nav-farm" onClose={onClose} />
      <Item to="/veteriner-bul" icon={Stethoscope} label="Veteriner Hekim Bul" testid="mobile-nav-vet-directory" onClose={onClose} />
      <Item to="/kooperatif" icon={Users} label="Kooperatif & Birlik" testid="mobile-nav-coop" onClose={onClose} />
      {user && (
        <Item to="/gorevlerim" icon={ListChecks} label="Görevlerim" testid="mobile-nav-staff-tasks" onClose={onClose} />
      )}
    </Group>

    {user && (
      <Group title="Hesabım">
        <Item to="/panel" icon={LayoutDashboard} label="Panelim" testid="mobile-nav-dashboard" onClose={onClose} />
        <Item to="/panel/favoriler" icon={Heart} label="Favorilerim" testid="mobile-nav-favorites" onClose={onClose} />
        <Item to="/panel/siparislerim" icon={TruckIcon} label="Siparişlerim" testid="mobile-nav-orders" onClose={onClose} />
        <Item to="/bildirimler" icon={Bell} label="Bildirim Tercihleri" testid="mobile-nav-notifications" onClose={onClose} />
        {isVet ? (
          <Item to="/veteriner" icon={Stethoscope} label="Veteriner Panelim" testid="mobile-nav-vet-dashboard" onClose={onClose} />
        ) : (
          <Item to="/veteriner/profil" icon={Stethoscope} label="Veteriner Ol" testid="mobile-nav-vet-panel" onClose={onClose} />
        )}
        {STAFF.includes(user.role) && (
          <Item to="/yonetim" icon={Shield} label="Yönetim Merkezi" testid="mobile-nav-admin" onClose={onClose} />
        )}
        <button
          onClick={onLogout}
          data-testid="sheet-logout"
          className="app-tap flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-left text-[15px] font-semibold text-destructive hover:bg-muted"
        >
          <LogOut className="h-5 w-5 shrink-0" /> Çıkış Yap
        </button>
      </Group>
    )}

    {!!cats?.length && (
      <Group title="Kategoriler">
        <div className="flex flex-wrap gap-2 px-3 pt-1">
          {cats.map((c) => (
            <Link
              key={c.id}
              to={`/ilanlar?category_id=${c.id}`}
              onClick={onClose}
              data-testid={`sheet-cat-${c.id}`}
              className="app-tap rounded-full border border-border bg-card px-3 py-2 text-xs font-semibold text-primary"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </Group>
    )}
  </BottomSheet>
);
